import React, { Component } from 'react';
import _ from 'lodash';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { prettyDuration } from "../components/common/TimeUtils"
import submenu from './menus/submenu-tasks'

dayjs.extend(relativeTime);

import CRUDPage from './CRUDPage';
import ObjectSearchHandler from '../components/common/ObjectSearchHandler';
import LegoContextSummary from '../components/lego/LegoContextSummary';
import DataProcessEditor from '../components/dataprocess/DataProcessEditor';
import DataTaskEditor from '../components/dataprocess/DataTaskEditor';
import { GenericDataTypeSummary } from '../components/dataprocess/GenericDataTypeSummary';
import { LegoStateSwitch } from '../components/lego/LegoStateSwitch';
import ModelContextEditor from '../components/lego/ModelContextEditor';
import { DataTaskTypeSelector } from '../components/tasks/DataTaskTypeSelector';
import SingleValueSelector from '../components/common/editors/SingleValueSelector';
import { VehicleSummary } from '../components/common/VehicleSummary';
import MongoDocEditor from '../components/lego/MongoDocEditor';
import { LegoFiltersBar } from '../components/common/LegoFiltersBar';
import { Link } from 'react-router-dom';
import { IconButton } from '../components/common/IconButton';
import JSONPreview from '../components/common/JSONPreview';

export default class DataTasksManager extends CRUDPage {
  constructor(props) {
    super(props, 'services/data-tasks');

    this.submenu = submenu;

    this.dataProcessDefinitionService = this.service('services/data-process-definition');
  }

  getSearchHandler() {
    const taskSearchFilters = {
      duration: {
        filter: n => (task => task.workedTotalTime && task.workedTotalTime > 1000 * parseFloat(n || '0')),
        description: 'Tasks with min duration (workedTotalTime) > N seconds (e.g. :duration=60)'
      },
    };

    return new ObjectSearchHandler(taskSearchFilters);
  }

  componentDidMount() {
    super.componentDidMount();
    if (!this.state.processDefinitionsById) {
      this.loadProcessDefinitions().catch(this.handleError);
    }
  }

  async loadProcessDefinitions() {
    this.processDefinitions = await this.dataProcessDefinitionService.find();
    this.setState({ processDefinitionsById: _.keyBy(this.processDefinitions, 'processType') });
  }

  getColumnsDefinition(objects) {
    return [
      { content: 'Id', className: '' },
      { content: 'Impact', className: '' },
      { content: 'Doer/State', className: 'text-center' },
      { content: 'Task', className: '' },
      { content: 'Context', className: '' },
      { content: 'Summary', className: 'text-center' },
    ];
  }

  getFacetFiltersComponent() {
    let facetFilters = this.state.facetFilters || {};

    const onTypeChange = (f) => this.onFiltersChange({ ...facetFilters, typeId: f });
    const onStateChange = (val) => this.onFiltersChange({ ...facetFilters, state: val });

    return <div className={'d-flex align-items-center'}>
      <span style={{ width: '250px' }} className={'zoom-90'}>
        <DataTaskTypeSelector value={facetFilters.typeId} onChange={onTypeChange} />
      </span>

      <span style={{ width: '180px' }} className={'zoom-90 ml-2'}>
        <SingleValueSelector value={facetFilters.state} onChange={onStateChange}
          placeholder={'State'} options={['complete', 'needsRevision']} />
      </span>

      <LegoFiltersBar fields={['_id', 'context']} filter={facetFilters} onChange={this.onFiltersChange.bind(this)} />
    </div>
  }

  getSelectionButtons(selection) {
    if (!selection?.length) return super.getSelectionButtons(selection);

    const sameBatch = new Set(selection.map(t => t.typeId)).size === 1;

    if (!sameBatch) {
      return super.getSelectionButtons(selection);
    }

    const idsParam = 'ids:' + selection.map(t => t._id).join('_+_');

    return <>
      {super.getSelectionButtons(selection)}
      <Link to={`/tasks/batches/${idsParam}`} className={'btn btn-link p-0'}>
        <IconButton icon={'play_circle_filled'}>
          Start batch of {selection.length} {selection[0].typeId}...
        </IconButton>
      </Link>
    </>;
  }

  getObjectEditorComponent(obj, defaultOnSaveCbk, defaultOnCancelCbk) {
    return <MongoDocEditor obj={obj} onSave={defaultOnSaveCbk} onCancel={defaultOnCancelCbk} />;
  }

  async startTask(task) {
    task.startedAt = new Date();
    await this.views.update(task._id, task);
    this.openEdit(task)
  }

  getObjectColumns(task, definition) {
    let { _id, impact, context, processType, typeId, batchId, input, output, notes, doer, state, startedAt, finishedAt, workedTotalTime, createdAt, updatedAt } = task;


    let realState = 'Not started';
    let extraStateData = null;
    let stateStyle = 'bg-light-secondary';
    let taskQuickActions = null;

    if (startedAt) {
      if (finishedAt) {
        realState = 'Finished';
        stateStyle = 'bg-light-success';
        extraStateData = <div className={'small text-info'}>In {prettyDuration(workedTotalTime)} </div>;
      } else {
        realState = 'In progress';
        stateStyle = 'bg-light-primary';
        extraStateData = <div className={'small text-secondary'}>Since {dayjs(startedAt).fromNow()}</div>;
      }
    } else if (state === 'complete') {
      realState = 'Finished';
      stateStyle = 'bg-light-success';
    } else {
      taskQuickActions =
        <span className={'btn btn-sm btn-success'} onClick={(e) => e.stopPropagation() + this.startTask(task)}>Start Task</span>;
    }

    if (state === 'needsRevision') {
      stateStyle = 'bg-light-warn';
    }

    let inputSummary;
    if (this.state.processDefinitionsById && this.state.processDefinitionsById[processType]) {
      inputSummary = _.map(input, (data, dataKey) => {
        const processDef = this.state.processDefinitionsById[processType];
        const typeDef = _.find(processDef.dataTypes, ({ name }) => name === dataKey);
        return <GenericDataTypeSummary key={dataKey} type={typeDef.type} value={data} />;
      });
    } else {
      inputSummary = <JSONPreview value={input} />;
    }

    let outputSummary;
    if (this.state.processDefinitionsById && this.state.processDefinitionsById[processType]) {
      outputSummary = _.map(output, (data, dataKey) => {
        const processDef = this.state.processDefinitionsById[processType];
        const typeDef = _.find(processDef.dataTypes, ({ name }) => name === dataKey);
        return <GenericDataTypeSummary key={dataKey} type={typeDef.type} value={data} />;
      });
    } else {
      outputSummary = <JSONPreview value={output} />;
    }

    return [
      <td key={'id'}>
        <div>
          <span className={'text-info small'} title={updatedAt}>{updatedAt ? dayjs(updatedAt).fromNow() : ''}</span>
        </div>
        <div className={'text-secondary small'}>{_id}</div>
      </td>,

      <td key={'impact'} className={'text-center'}>{(impact && impact.toFixed(1)) || null}</td>,

      <td key={'finishedState'} className={`text-center ${stateStyle}`}>
        {doer ? <div>{doer}</div> : null}
        <span className={'text-secondary'}>{realState}</span>
        {extraStateData}
      </td>,

      <td key={'processName'} className={`${stateStyle}`}>
        <strong className={'text-primary'}>{typeId}</strong>
        <div className={'small text-secondary'}>{processType || batchId}</div>
      </td>,

      <td key={'ctx'} style={{ maxWidth: '300px', wordBreak: 'break-all' }} className={`${stateStyle}`}>
        <LegoContextSummary context={context} wrap={false} />
      </td>,

      <td key={'summary'} className={'cursor-pointer'} onClick={() => this.openEdit(task)}>
        <div className={'d-flex flex-wrap align-items-start'}>

          <div className={'ng-light'}>
            <span className={'text-left align-middle'}>{inputSummary}</span>
          </div>

          <span className={'col-1'}>
            <i className={'material-icons align-middle mr-2 ml-2'}>arrow_right_alt</i>
          </span>

          <div className={''}>
            <span className={'align-middle'}>{taskQuickActions}</span>
            <span className={'text-left align-middle'}>{outputSummary}</span>
          </div>
        </div>
      </td>
    ];
  }
}
